import React, { useEffect, useRef, useState } from 'react';
import '../sales.module.css';
import { useConnectedUser, UserType } from '../../../context/connected-user.context';
import {
    isRoundType,
    RoundStakingStatus,
    RoundType,
    StakingInfo,
    toRoundStatus,
    toRoundType
} from '../../../web3/model/staking.model';
import { Button, Web3Button } from '../../../design-system/button';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { RoundInfo } from '../../../web3/model/round.model';
import { StakingDataDisplay, StakingDataHeader } from '../staking/staking-view';
import { Observable } from 'rxjs';
import { delay } from '../../../utils/async-utils';
import Decimal from 'decimal.js';
import { SaleRoundWithStaking } from '../../../web3/contract';
import { stat } from 'fs';
import { log } from 'console';

// Type definition for SaleRoundCard props
interface SaleRoundCardProps {
    saleRoundWithStaking: SaleRoundWithStaking;
    userType: UserType;
}

// Modern SaleRoundCard component with improved UI
const SaleRoundCard: React.FC<SaleRoundCardProps> = ({ saleRoundWithStaking, userType }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [cdkAmount, setCdkAmount] = useState(0);
    const connectedUser = useConnectedUser();
    const cdkContract = connectedUser.contract;
    
    // const status = toRoundStatus(saleRoundWithStaking.saleRound.roundDetails.roundStatus);
    const roundId = saleRoundWithStaking.saleRound.roundId;
    
    // // Determine status indicator styles based on round status
    // const getStatusIndicator = () => {
    //     switch (status) {
    //         case "ON":
    //             return {
    //                 badge: 'bg-emerald-100 text-emerald-800',
    //                 dot: 'bg-emerald-500',
    //                 text: 'ON'
    //             };
    //         case "OFF":
    //             return {
    //                 badge: 'bg-red-100 text-red-700',
    //                 dot: 'bg-red-500',
    //                 text: 'OFF'
    //             };
    //         default:
    //             return {
    //                 badge: 'bg-amber-100 text-amber-800',
    //                 dot: 'bg-amber-500',
    //                 text: 'PENDING'
    //             };
    //     }
    // };
    
    // const statusIndicator = getStatusIndicator();
    
    const buyCDKTokens = async () => {
        if (cdkContract) {
            await cdkContract.buyCDKTokens(
                saleRoundWithStaking.saleRound.roundId, 
                cdkAmount, 
                Number(saleRoundWithStaking.saleRound.roundDetails.tokenPrice)
            );
        }
    }
    
    const onBuySuccess = () => {
        toast.success('CDK tokens bought successfully!');
        connectedUser.reloadAllBalances();
        setIsExpanded(false);
        setCdkAmount(0);
    }
    
    return (
        <div className="bg-white rounded-xl shadow-md overflow-hidden transition-all duration-300 hover:shadow-lg border border-gray-100 flex flex-col h-full">
            {/* Card Header */}
            <div className="p-4 md:p-5 border-b border-gray-100">
                <div className="flex justify-between items-center">
                    <h3 className="text-sm md:text-base font-bold text-gray-800">Round {roundId}</h3>
                    {/* <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full ${statusIndicator.badge}`}>
                        <span className={`h-2 w-2 rounded-full ${statusIndicator.dot}`}></span>
                        <span className="text-xs font-semibold">{statusIndicator.text}</span>
                    </div> */}
                </div>
            </div>
            
            {/* Card Body */}
            <div className="px-4 md:px-5 py-4 flex-grow">
                <div className="flex flex-col gap-3">
                    {/* Available CDK */}
                    <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Available CDK:</span>
                        <span className="font-semibold text-gray-900">{saleRoundWithStaking.saleRound.roundDetails.allocation.toString().replace(/,|\.\d+/g, '')}</span>
                    </div>
                    
                    {/* Token Price */}
                    <div className="flex justify-between items-center pt-3 border-t border-gray-100">
                        <span className="text-sm text-gray-600">Token Price:</span>
                        <div className="flex items-center">
                            <span className="text-lg font-bold text-orange-600">{saleRoundWithStaking.saleRound.roundDetails.tokenPrice}</span>
                            <span className="ml-1 text-xs md:text-sm font-medium text-gray-500">USDC</span>
                        </div>
                    </div>

                    {
                        saleRoundWithStaking.stakingRound && (
                            <>
                            <div className="flex justify-between items-center pt-3 border-t border-gray-100">
                                <div className='flex-1 relative'>
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm text-gray-600">Cliff Period:</span>
                                        <span className="font-semibold text-gray-900">{saleRoundWithStaking.stakingRound.cliffPeriod} months</span>
                                    </div>
                                </div>
                            </div>
                            <div className="flex justify-between items-center pt-3 border-t border-gray-100">
                                <div className='flex-1 relative'>
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm text-gray-600">Vesting Period:</span>
                                        <span className="font-semibold text-gray-900">{saleRoundWithStaking.stakingRound.vestingPeriod} months</span>
                                    </div>
                                </div>
                            </div>
                            </>
                        )
                    }

                </div>
            </div>
            
            {/* Card Footer */}
            <div className="p-4 md:p-5 bg-gray-50 border-t border-gray-100">
             {!isExpanded ? (
                    <Button
                        onClick={() => setIsExpanded(true)}
                        type="gradient"
                        gradientColors={['#ff0000', '#ff8c00']}
                        text='Buy CDK Tokens'
                    />
                ) : (
                    <div className="space-y-4 rounded-2xl border border-orange-200 bg-orange-50 p-5 shadow-md">
                        {/* Header */}
                        <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                                <h4 className="font-semibold text-base text-orange-800">Buy CDK Tokens</h4>
                            </div>
                            <button
                                onClick={() => setIsExpanded(false)}
                                className="p-1 rounded-full bg-orange-100 hover:bg-orange-200 text-orange-600 transition-colors"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                            </button>
                        </div>
                        
                        {/* Input Field */}
                        <div className="flex-1 relative">
                            <input
                                type="number"
                                value={cdkAmount}
                                onChange={(e) => setCdkAmount(Number(e.target.value))}
                                className="block w-full pl-3 pr-12 py-2.5 text-base border border-orange-300 rounded-lg focus:ring-orange-500 focus:border-orange-500 transition-all"
                                style={{ backgroundColor: "#ffffff" }}
                                placeholder="Enter amount"
                                min="0"
                            />
                            <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-orange-400 text-sm">CDK</span>
                        </div>
                        
                        {/* Total - Now under the input */}
                        <div className="flex justify-between items-center px-1">
                            <span className="text-sm text-gray-600">Total Cost:</span>
                            <div className="font-bold text-orange-600 text-base">
                                {new Decimal(saleRoundWithStaking.saleRound.roundDetails.tokenPrice)
                                .times(cdkAmount)
                                .toFixed(5)} USDC
                            </div>
                        </div>
                        
                        {/* Buy Button */}
                        <Web3Button
                            text={`Buy ${cdkAmount} CDK tokens`}
                            web3Action={() => buyCDKTokens()}
                            onSuccess={onBuySuccess}
                            
                        />
                    </div>
                )}
            </div>
        </div>
    );
};
export type ListSaleRoundsProps = {
    saleRoundsWithStaking: SaleRoundWithStaking[];
    onRoundChange: (roundId: string | 'all') => void;
    userType: UserType;
};

const ListSaleRounds: React.FC<ListSaleRoundsProps> = (props) => {
    const cdkContract = useConnectedUser().contract;
    const navigate = useNavigate();
    const [currentRoundStakingInfo, setCurrentRoundStakingInfo] = React.useState<StakingInfo | undefined>(undefined);
    const goToSaleRoundPage = (roundId: string) => {
        navigate(`/sale-round/${roundId}?userType=${props.userType}`);
    };
    
    const loadAssociatedStakingRound = async (saleRoundId: string) => {
        if (cdkContract) {
            const stakingRoundId = await cdkContract.roundFacet.getStakingId(saleRoundId);
            const stakingDetails = await cdkContract.stakingFacet.getStakingRoundDetails(stakingRoundId)
            setCurrentRoundStakingInfo({ stakingId: stakingRoundId, stakingDetails });
        }
    }
    
    if (props.saleRoundsWithStaking.length === 0) {
        return (
            <div className="p-12 text-center bg-gray-50 rounded-2xl shadow-sm border border-gray-100">
                <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                <h1 className="text-xl font-medium text-gray-700">No sale rounds available</h1>
                <p className="text-gray-500 mt-2">Check back later for new opportunities</p>
            </div>
        );
    }
    
    return (
        <div className="px-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {props.saleRoundsWithStaking.map((saleRound, index) => (
                    <SaleRoundCard 
                        key={index} 
                        saleRoundWithStaking={saleRound} 
                        userType={props.userType} 
                    />
                ))}
            </div>
            
            {currentRoundStakingInfo && (
                <div className="mt-8 bg-white rounded-2xl shadow-md overflow-hidden border border-gray-100">
                    <div className="px-6 py-4 bg-indigo-50 border-b border-indigo-100">
                        <h3 className="text-lg font-medium text-indigo-800">Staking Information</h3>
                    </div>
                    <div className="p-6">
                        <table className="min-w-full divide-y divide-gray-200">
                            <StakingDataHeader />
                            <StakingDataDisplay stakingInfo={currentRoundStakingInfo} />
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export type ListAllSaleRoundsProps = {
    userType: UserType;
    reloadAllRound?: Observable<void>;
    status?: RoundStakingStatus
    roundType?: RoundType
};

export const ListAllSaleRounds: React.FC<ListAllSaleRoundsProps> = (
    { userType, reloadAllRound, status, roundType }
) => {
    const cdkContract = useConnectedUser().contract;
    const [salesRounds, setSalesRounds] = React.useState<SaleRoundWithStaking[]>([]);
    const [isLoading, setIsLoading] = React.useState<boolean>(true);
    const reloadAllRoundRef = useRef(reloadAllRound);
    
    const loadAllSaleRounds = async () => {
        if (cdkContract) {
            setIsLoading(true);
            const data = await cdkContract.loadAllSaleRoundsWithStakingDetails(status, roundType);
            setSalesRounds(data);
            setIsLoading(false);
        }
    }

    const onRoundChange = async (roundId: string | 'all') => {
        if ('all' === roundId) {
            toast.success('All rounds updated successfully');
        } else {
            toast.success('Round ' + roundId + ' updated successfully');
        }
        await loadAllSaleRounds();
    };
    
    useEffect(() => {
        loadAllSaleRounds();
    }, [cdkContract]);
    
    if (reloadAllRoundRef.current) {
        reloadAllRoundRef.current.subscribe(() => {
            loadAllSaleRounds();
        });
    }
    
    return (
        <div className="py-8">
            <div className="mb-8">
                <div className="flex justify-between items-center">
                    <div>
                        <h2 className="text-3xl font-bold text-gray-800">Sale Rounds</h2>
                        <p className="text-gray-600 mt-1">Browse available token sale rounds</p>
                    </div>
                    <button 
                        onClick={() => loadAllSaleRounds()}
                        className="flex items-center gap-2 px-4 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-lg transition-colors"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Refresh
                    </button>
                </div>
            </div>
            
            {isLoading ? (
                <div className="flex justify-center items-center p-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
                </div>
            ) : (
                <ListSaleRounds saleRoundsWithStaking={salesRounds} onRoundChange={onRoundChange} userType={userType} />
            )}
        </div>
    );
};

export const ListActiveSaleRoundsForUser: React.FC = () => {
    return (<ListAllSaleRounds userType='USER' status={RoundStakingStatus.ON} roundType={RoundType.PUBLIC} />)
}

export const CurrentSaleRoundForBuy: React.FC = () => {
    const cdkContract = useConnectedUser().contract;
    const [allRounds, setAllRounds] = React.useState<SaleRoundWithStaking[]>([]);
    const [isLoading, setIsLoading] = React.useState<boolean>(true);
    
    const loadCurrentRound = async () => {
        setIsLoading(true);
        if (cdkContract) {
            // Also load all available rounds
            const rounds = await cdkContract.loadAllSaleRoundsWithStakingDetails();
            // Filter for active rounds only if needed

            const activePublicRounds = rounds.filter(data => {
                const round = data.saleRound
                // Convert BigInt to Number if needed
                const roundStatus = typeof round.roundDetails.roundStatus === 'bigint'
                    ? Number(round.roundDetails.roundStatus)
                    : round.roundDetails.roundStatus;

                const roundType = typeof round.roundDetails.roundType === 'bigint'
                    ? Number(round.roundDetails.roundType)
                    : round.roundDetails.roundType;

                return roundStatus === RoundStakingStatus.ON && roundType === RoundType.PUBLIC;
            });
            setAllRounds(activePublicRounds)
        }
        setIsLoading(false);
    }
    
    useEffect(() => {
        loadCurrentRound();
    }, [cdkContract]);
    
    if (isLoading) {
        return (
            <div className="flex justify-center items-center p-12">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
            </div>
        );
    }
    
    if (allRounds.length === 0) {
        return (
            <div className="p-12 text-center bg-gray-50 rounded-2xl shadow-sm border border-gray-100">
                <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                <h1 className="text-xl font-medium text-gray-700">No sale rounds available</h1>
                <p className="text-gray-500 mt-2">Check back later for new opportunities</p>
            </div>
        );
    }
    
    return (
        <>
            <div className="mb-8">
                <h2
                    className="text-3xl font-bold bg-clip-text text-transparent"
                    style={{
                    backgroundImage: 'linear-gradient(to right, #ff0000, #ff8c00)'
                    }}
                >
                    Available Public Sale Rounds
                </h2>
                <p className="text-white-600 mt-2">Select a round to purchase tokens</p>
            </div>
            
            <ListSaleRounds 
                saleRoundsWithStaking={allRounds} 
                onRoundChange={() => {}} 
                userType='USER'
            />
        </>
    );
}
